/**
 * SUPPORT TICKETS MOCK DATA
 *
 * Centralized mock data for support tickets.
 * Uses mockDataManager for localStorage persistence.
 *
 * PE-764: Support tickets notifications for System Admin bell
 */

import { createPersistedMockData } from '../mockDataManager';

// ============================================
// DATE HELPERS
// ============================================
const now = new Date();
const daysAgo = (days: number) => {
  const date = new Date(now);
  date.setDate(date.getDate() - days);
  return date.toISOString();
};
const hoursAgo = (hours: number) => {
  const date = new Date(now);
  date.setHours(date.getHours() - hours);
  return date.toISOString();
};

/**
 * Support Ticket Interface
 */
export interface SupportTicket {
  id: string;
  title: string;
  description: string;
  priority: 'Low' | 'Medium' | 'High' | 'Critical';
  status: 'Open' | 'In Progress' | 'Pending' | 'Resolved' | 'Closed';
  category: 'Bug' | 'Feature Request' | 'System Issue' | 'User Access' | 'Performance' | 'Other';
  submittedBy: string;
  assignedTo?: string;
  createdAt: string;
  updatedAt: string;
  linearTicketId?: string;
  linearUrl?: string;
  attachments: string[];
  comments: Array<{
    id: string;
    author: string;
    content: string;
    timestamp: string;
  }>;
}

/**
 * DEFAULT SUPPORT TICKETS
 */
export const defaultSupportTickets: SupportTicket[] = [
  {
    id: 'TICK-001',
    title: 'Unable to access client records',
    description: 'Getting 403 error when trying to view client details in the CRM system. This affects multiple team members.',
    priority: 'High',
    status: 'In Progress',
    category: 'System Issue',
    submittedBy: 'Sarah Johnson',
    assignedTo: 'Mike Wilson',
    createdAt: daysAgo(5),
    updatedAt: hoursAgo(2),
    linearTicketId: 'RGL-123',
    linearUrl: 'https://linear.app/regal/issue/RGL-123',
    attachments: ['error-screenshot.png'],
    comments: [
      {
        id: 'c1',
        author: 'Mike Wilson',
        content: 'Investigating the issue. Looks like a permission problem with the database.',
        timestamp: daysAgo(5).split('T')[0] + 'T11:00:00Z'
      }
    ]
  },
  {
    id: 'TICK-002',
    title: 'Feature Request: Bulk client export',
    description: 'Need ability to export multiple client records at once for reporting purposes.',
    priority: 'Medium',
    status: 'Open',
    category: 'Feature Request',
    submittedBy: 'John Smith',
    createdAt: daysAgo(7),
    updatedAt: daysAgo(7),
    linearTicketId: 'RGL-124',
    linearUrl: 'https://linear.app/regal/issue/RGL-124',
    attachments: [],
    comments: []
  },
  {
    id: 'TICK-003',
    title: 'Dashboard loading slowly',
    description: 'The main dashboard takes 10+ seconds to load, especially in the morning.',
    priority: 'Medium',
    status: 'Resolved',
    category: 'Performance',
    submittedBy: 'Emily Davis',
    assignedTo: 'System Admin',
    createdAt: daysAgo(10),
    updatedAt: daysAgo(8),
    linearTicketId: 'RGL-121',
    linearUrl: 'https://linear.app/regal/issue/RGL-121',
    attachments: [],
    comments: [
      {
        id: 'c2',
        author: 'System Admin',
        content: 'Optimized database queries. Should be faster now.',
        timestamp: daysAgo(8).split('T')[0] + 'T16:30:00Z'
      }
    ]
  }
];

/**
 * PERSISTED STORAGE
 */
export const persistedSupportTickets = createPersistedMockData<SupportTicket>(
  'crm_support_tickets',
  defaultSupportTickets
);

/**
 * BACKWARD COMPATIBILITY
 */
export const mockSupportTickets = defaultSupportTickets;
